"use client";
import {
  Nav,
  NavDropdown,
  Navbar,
  Table,
  OverlayTrigger,
} from "react-bootstrap";
import { Link, useNavigate, useParams } from "react-router-dom";
import Tooltip from "react-bootstrap/Tooltip";

import {
  API_USER,
  BRANCH_ADMIN,
  BRANCH_CONSULTANT,
  END_USER,
  INTERNAL_ADMIN,
  bGId,
} from "common/constants/constants";
import { DotsThree } from "phosphor-react";
import PropTypes from "prop-types";
import SimpleScrollBar from "simplebar-react";
import "components/common/dynamicTable/dynamicTable.scss";
import { useTranslation } from "react-i18next";
import { downloadApiUserStats } from "services/UserManagementServices";
function TableCommon(props) {
  const {
    heading,
    name,
    body,
    permissionToEdit,
    tableStyle,
    detailLink,
    deleteTeamData,
    updateTeamData,
    pathIds,
    firstName,
    lastName,
    description,
    education,
    ...rest
  } = props;
  const { t } = useTranslation();
  const { id: branchIdParam } = useParams();
  const navigate = useNavigate();

  const flattenColumns = (columns) => {
    const flattened = [];
    const traverse = (cols) => {
      cols.forEach((col) => {
        if (col.children && col.children.length > 0) {
          traverse(col.children);
        } else {
          flattened.push(col);
        }
      });
    };
    traverse(columns);
    return flattened;
  };

  const calculateColSpan = (column) => {
    if (!column.children || column.children.length === 0) {
      return 1;
    }
    return column.children.reduce(
      (sum, child) => sum + calculateColSpan(child),
      0
    );
  };

  const getMaxDepth = (columns) => {
    let maxDepth = 1;
    const traverse = (cols, depth = 1) => {
      cols.forEach((col) => {
        if (col.children && col.children.length > 0) {
          traverse(col.children, depth + 1);
        } else {
          maxDepth = Math.max(maxDepth, depth);
        }
      });
    };
    traverse(columns);
    return maxDepth;
  };

  const renderHeaderRows = () => {
    const isNestedFormat =
      heading &&
      heading.length > 0 &&
      typeof heading[0] === "object" &&
      heading[0].title;

    if (!isNestedFormat) {
      // Original flat header format
      return (
        <tr>
          {heading?.map((item) => {
            return (
              <th
                className="text-uppercase"
                key={item}
                style={{
                  width: `calc(1 * 100% / ${heading.length} )`,
                }}
                {...rest}
              >
                {item}
              </th>
            );
          })}
          <th style={{ zIndex: "5" }}></th>
        </tr>
      );
    }

    const maxDepth = getMaxDepth(heading);
    const flatColumns = flattenColumns(heading);
    const headerRows = Array(maxDepth)
      .fill(null)
      .map(() => []);

    const renderColumn = (column, level, colIndex) => {
      const colSpan = calculateColSpan(column);
      const rowSpan =
        column.children && column.children.length > 0 ? 1 : maxDepth - level;

      headerRows[level].push(
        <th
          key={`${level}-${colIndex}-${column.title || column}`}
          className={level < maxDepth - 1 ? "text-uppercase": ""}
          colSpan={colSpan}
          rowSpan={rowSpan}
          style={{
            width: `calc(${colSpan} * 100% / ${flatColumns.length + 1})`,
            textAlign: level < maxDepth - 1 ? "left" : "left",
            verticalAlign: "top",
            fontWeight: level < maxDepth - 1 ? "bold" : "normal",
            borderBottom:
              level < maxDepth - 1 ? "1px solid #dee2e6" : undefined,
          }}
          {...rest}
        >
          {column.title || column}
        </th>
      );

      if (column.children && column.children.length > 0) {
        column.children.forEach((child, childIndex) => {
          renderColumn(child, level + 1, colIndex + childIndex);
        });
      }
    };

    // Render columns starting from level 0
    heading.forEach((column, index) => {
      renderColumn(column, 0, index);
    });

    // Add actions column only to the first row
    headerRows[0].push(
      <th key="actions" style={{ zIndex: "5" }} rowSpan={maxDepth}></th>
    );

    return headerRows.map((row, index) => <tr key={index}>{row}</tr>);
  };

  const handleDetails = (id, bankId = "") => {
    if (detailLink) {
      navigate(`/branches/${branchIdParam}/users/${id}`);
    } else {
      if (name === "branches") {
        navigate(`/branches/${id}`, { state: bankId });
      } else {
        navigate(`/user-management/detail/${id}`);
      }
    }
  };
  const exportUserDetail = async (userId) => {
    const response = await downloadApiUserStats(userId);
    const result = { status: 200 };
    const url = URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "Bericht" + ".xlsx");
    document.body.appendChild(link);
    link.click();
    return result;
  };
  const getUserRole = (key) => {
    if (key === INTERNAL_ADMIN) return t("#IA");
    else if (key === BRANCH_ADMIN) return t("#EA");
    else if (key === BRANCH_CONSULTANT) return t("#EC");
    else if (key === END_USER) return t("#EU");
    else if (key === API_USER) return t("#AU");
  };
  const formatDate = (date) => {
    if (date) {
      const newDate = new Date(date);
      let day = newDate.getDate();
      let month = newDate.getMonth() + 1;
      const year = newDate.getFullYear();
      day = day > 9 ? day : "0" + day;
      month = month > 9 ? month : "0" + month;
      return day + "." + month + "." + year;
    } else {
      return "--";
    }
  };

  const cellStyle = {
    margin: "0px",
    width: "100px",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  };
  const vdsUserTableBody = () => {
    return (
      <>
        {body?.map((item, index) => {
          const {
            firstName,
            lastName,
            email,
            lastActivity,
            isActive,
            role,
            id,
            products,
            consultantName,
            firstPayment,
          } = item;
          let d, date;
          if (lastActivity) {
            d = new Date(lastActivity);

            date = d.toString().split(" ").slice(1, 4).join(". ");
          } else {
            date = "--";
          }

          return (
            <>
              <tr key={index}>
                <td
                  className="text-uppercase"
                  style={{ width: `calc(1 * 100% / ${heading.length})` }}
                >
                  <div className="first-cell">
                    <div>
                      <OverlayTrigger
                        placement="top"
                        overlay={
                          <Tooltip id="name-tooltip">
                            {firstName || "--"} {lastName || "--"}
                          </Tooltip>
                        }
                      >
                        <p style={cellStyle}>{firstName || "--"}</p>
                      </OverlayTrigger>
                      <br />
                      <span style={{ color: "#979797" }}>
                        <p style={cellStyle}>{lastName || "--"}</p>
                      </span>
                    </div>
                  </div>
                </td>
                <td>
                  {/* <p style={cellStyle}>{email}</p> */}
                  <OverlayTrigger
                    placement="top"
                    overlay={<Tooltip id="email-tooltip">{email}</Tooltip>}
                  >
                    <p style={cellStyle}>{email}</p>
                  </OverlayTrigger>
                </td>
                <td>
                  <p style={cellStyle}>{date || "--"}</p>
                </td>
                <td>{isActive ? t("active") : t("deactive")}</td>

                <td
                  className="text-uppercase"
                  style={{ width: `calc(1 * 100% / ${heading.length})` }}
                >
                  {getUserRole(role)}
                </td>
                {[BRANCH_CONSULTANT].includes(name) && (
                  <td
                    className="text-uppercase"
                    style={{ width: `calc(1 * 100% / ${heading.length})` }}
                  >
                    {formatDate(firstPayment)}
                  </td>
                )}
                {[BRANCH_ADMIN, BRANCH_CONSULTANT].includes(name) && (
                  <td style={{ width: `calc(1 * 100% / ${heading.length})` }}>
                    {products.length > 0 ? (
                      products
                        .filter((product, index, arr) => {
                          return (
                            arr.findIndex(
                              (el) => el.productId === product.productId
                            ) === index
                          );
                        })
                        .map((product, index) => (
                          <img
                            src={`/assets/icons${product.imageUrl}`}
                            width={30}
                            height={30}
                            key={index}
                            className="me-2"
                          />
                        ))
                    ) : (
                      <span>{t("no-product-assigned")}</span>
                    )}
                  </td>
                )}
                {[BRANCH_CONSULTANT].includes(name) && (
                  <td
                    className="text-uppercase"
                    style={{ width: `calc(1 * 100% / ${heading.length})` }}
                  >
                    <div className="detail-cell">{consultantName || "--"}</div>
                  </td>
                )}
                <td
                  className="text-uppercase"
                  style={{ width: `calc(1 * 100% / ${body.length})` }}
                >
                  <div className="detail-cell">
                    <div>
                      <button
                        className="showdetails-btn"
                        onClick={() => handleDetails(id)}
                      >
                        {t("show-details")}
                      </button>
                    </div>
                  </div>
                </td>
                <td
                  className="text-uppercase"
                  style={{ width: `calc(1 * 100% / ${body.length})` }}
                >
                  {permissionToEdit ? (
                    <Navbar
                      bg="white"
                      expand="lg"
                      style={{ display: "contents" }}
                    >
                      <div>
                        <Navbar.Toggle aria-controls="navbar-dark-example" />
                        <Navbar.Collapse>
                          <Nav>
                            <NavDropdown
                              title={
                                <DotsThree
                                  width={50}
                                  r={25}
                                  size={20}
                                  className="icon"
                                />
                              }
                            >
                              <NavDropdown.Item>
                                <Link
                                  to={`/user-management/edit-user/${id}`}
                                  //state={{ bankId, bGId }}
                                  style={{
                                    textDecoration: "none",
                                    color: "#000000",
                                  }}
                                >
                                  {t("edit")}
                                </Link>
                              </NavDropdown.Item>
                              {/* <NavDropdown.Item>Delete</NavDropdown.Item> */}
                            </NavDropdown>
                          </Nav>
                        </Navbar.Collapse>
                      </div>
                    </Navbar>
                  ) : (
                    <DotsThree width={50} r={25} size={20} className="icon" />
                  )}
                </td>
              </tr>
            </>
          );
        })}
      </>
    );
  };
  const vdsUserTableBodyAPI = () => {
    return (
      <>
        {body?.map((item, index) => {
          const {
            firstName,
            lastName,
            email,
            lastActivity,
            organization,
            isActive,
            role,
            id,
            companyName,
            createdAt,
          } = item;
          let d, date;
          if (createdAt) {
            d = new Date(createdAt);

            date = d.toString().split(" ").slice(1, 4).join(". ");
          } else {
            date = "--";
          }

          return (
            <>
              <tr key={index}>
                <td
                  className="text-uppercase"
                  style={{ width: `calc(1 * 100% / ${heading.length})` }}
                >
                  <div className="first-cell">
                    <div>
                      <OverlayTrigger
                        placement="top"
                        overlay={
                          <Tooltip id="name-tooltip">
                            {firstName || "--"} {lastName || "--"}
                          </Tooltip>
                        }
                      >
                        <p style={cellStyle}>{firstName || "--"}</p>
                      </OverlayTrigger>
                      <br />
                      <span style={{ color: "#979797" }}>
                        <p style={cellStyle}>{lastName || "--"}</p>
                      </span>
                    </div>
                  </div>
                </td>
                <td>
                  {/* <p style={cellStyle}>{email}</p> */}
                  <OverlayTrigger
                    placement="top"
                    overlay={<Tooltip id="email-tooltip">{email}</Tooltip>}
                  >
                    <p style={cellStyle}>{email}</p>
                  </OverlayTrigger>
                </td>
                <td>
                  <OverlayTrigger
                    placement="top"
                    overlay={
                      <Tooltip id="email-tooltip">{companyName}</Tooltip>
                    }
                  >
                    <p style={cellStyle}>{companyName}</p>
                  </OverlayTrigger>
                </td>
                <td>
                  <p style={cellStyle}>{date || "--"}</p>
                </td>
                <td>{isActive ? t("active") : t("deactive")}</td>
                <td
                  className="text-uppercase"
                  style={{ width: `calc(1 * 100% / ${heading.length})` }}
                >
                  {getUserRole(role)}
                </td>
                <td
                  className="text-uppercase"
                  style={{ width: `calc(1 * 100% / ${heading.length})` }}
                >
                  <div className="detail-cell">
                    <div>
                      <button
                        className="showdetails-btn"
                        onClick={() => handleDetails(id)}
                      >
                        {t("show-details")}
                      </button>
                    </div>
                  </div>
                </td>
                <td
                  className="text-uppercase"
                  style={{ width: `calc(1 * 100% / ${body.length})` }}
                >
                  {permissionToEdit ? (
                    <Navbar
                      bg="white"
                      expand="lg"
                      style={{ display: "contents" }}
                    >
                      <div>
                        <Navbar.Toggle aria-controls="navbar-dark-example" />
                        <Navbar.Collapse>
                          <Nav>
                            <NavDropdown
                              title={
                                <DotsThree
                                  width={50}
                                  r={25}
                                  size={20}
                                  className="icon"
                                />
                              }
                            >
                              <NavDropdown.Item
                                onClick={() => exportUserDetail(id)}
                              >
                                {t("export")}
                              </NavDropdown.Item>
                              {/* <NavDropdown.Item>Delete</NavDropdown.Item> */}
                            </NavDropdown>
                          </Nav>
                        </Navbar.Collapse>
                      </div>
                    </Navbar>
                  ) : (
                    <DotsThree width={50} r={25} size={20} className="icon" />
                  )}
                </td>
              </tr>
            </>
          );
        })}
      </>
    );
  };
  const branchesTableBody = () => {
    return (
      <>
        {body?.map((item) => {
          const { branch, products } = item;
          const {
            branchId,
            bankId,
            branchName,
            branchLocation,
            userCount,

            b2bBasicCount,
            b2bCount,
            b2bPremiumCount,
            b2bProCount,
            b2cCount,
            balanceTotal,
            esgQuestionnaires,
          } = branch;
          return (
            <tr
              key={branchName}
              onDoubleClick={() => handleDetails(branchId, bankId)}
              style={{ cursor: "pointer" }}
            >
              <td>
                <div className="first-cell">
                  <div>
                    <p style={cellStyle}>{branchName}</p>
                    {/* <br /> */}
                    <span style={{ color: "#979797" }}>
                      <p style={cellStyle}>{branchLocation}</p>
                    </span>
                  </div>
                </div>
              </td>
              <td>{userCount}</td>
              <td>{b2bCount}</td>
              <td>{b2bBasicCount}</td>
              <td>{b2bPremiumCount}</td>
              <td>{b2bProCount}</td>
              <td>{b2cCount}</td>
              <td>{esgQuestionnaires}</td>
              {/* <td>{balanceTotal + " €"}</td> */}
              <td>
                {products.length > 0 ? (
                  products
                    .filter((product, index, arr) => {
                      return (
                        arr.findIndex(
                          (el) => el.productId === product.productId
                        ) === index
                      );
                    })
                    .map((product, index) => (
                      <img
                        src={`/assets/icons${product.imageUrl}`}
                        width={30}
                        height={30}
                        key={product.productId}
                        className="me-2"
                      />
                    ))
                ) : (
                  <span>{t("no-product-added")}</span>
                )}
              </td>

              <td>
                <div className="detail-cell">
                  <div>
                    <button
                      className="showdetails-btn"
                      onClick={() => handleDetails(branchId, bankId)}
                    >
                      {t("show-details")}
                    </button>
                  </div>
                </div>
              </td>
              <td>
                {permissionToEdit ? (
                  <Navbar
                    bg="#f5f5f5"
                    expand="lg"
                    style={{ display: "contents", zIndex: 99999 }}
                  >
                    <div>
                      <Navbar.Toggle aria-controls="navbar-dark-example" />
                      <Navbar.Collapse>
                        <Nav>
                          <NavDropdown
                            title={
                              <DotsThree
                                width={50}
                                r={25}
                                size={20}
                                className="icon"
                              />
                            }
                          >
                            <NavDropdown.Item
                              style={{
                                backgroundColor: "transparent",
                              }}
                            >
                              <Link
                                to={`/branches/edit-branch/${branchId}`}
                                state={{ bankId, bGId }}
                                style={{
                                  textDecoration: "none",
                                  color: "#000000",
                                }}
                              >
                                {t("edit")}
                              </Link>
                            </NavDropdown.Item>
                          </NavDropdown>
                        </Nav>
                      </Navbar.Collapse>
                    </div>
                  </Navbar>
                ) : (
                  <DotsThree width={50} r={25} size={20} className="icon" />
                )}
              </td>
            </tr>
          );
        })}
      </>
    );
  };
  const TeamsTableBody = () => {
    return (
      <>
        {body?.map((item, index) => {
          const { teamMemberId } = item;
          const branchId = pathIds.branchId; // Declare branchId
          const bankId = pathIds.bankId; // Declare bankId
          return (
            <tr
              key={index}
              onDoubleClick={() => handleDetails(branchId, bankId)}
              style={{ cursor: "pointer" }}
            >
              <td>
                <div className="first-cell">
                  <p
                    style={cellStyle}
                    title={item?.firstName + " " + item?.lastName}
                  >
                    {item?.firstName} {item?.lastName}
                  </p>
                </div>
              </td>
              <td>
                <div className="first-cell">
                  <p style={cellStyle} title={item?.description}>
                    {item?.description}
                  </p>
                </div>
              </td>

              <td>
                <div className="detail-cell">
                  <div>
                    <button
                      className="showdetails-btn"
                      onClick={() => {
                        navigate(`/branch-detail/teams/${teamMemberId}`, {
                          state: { pathIds, teamData: item, teamLogo: item },
                        });
                      }}
                    >
                      {t("show-details")}
                    </button>
                  </div>
                </div>
              </td>
              <td>
                {permissionToEdit ? (
                  <Navbar
                    bg="#f5f5f5"
                    expand="lg"
                    style={{ display: "contents", zIndex: 99999 }}
                  >
                    <div>
                      <Navbar.Toggle aria-controls="navbar-dark-example" />
                      <Navbar.Collapse>
                        <Nav>
                          <NavDropdown
                            title={
                              <DotsThree
                                width={50}
                                r={25}
                                size={20}
                                className="icon"
                              />
                            }
                          >
                            <NavDropdown.Item
                              style={{
                                backgroundColor: "transparent",
                              }}
                            >
                              <Link
                                to={`/branch-detail/teams/createNewMember/${teamMemberId}`}
                                state={{ pathIds: pathIds, item: item }}
                                style={{
                                  textDecoration: "none",
                                  color: "#000000",
                                }}
                              >
                                {t("edit")}
                              </Link>
                            </NavDropdown.Item>
                            <NavDropdown.Item
                              onClick={() => {
                                deleteTeamData(teamMemberId);
                              }}
                            >
                              Delete
                            </NavDropdown.Item>
                          </NavDropdown>
                        </Nav>
                      </Navbar.Collapse>
                    </div>
                  </Navbar>
                ) : (
                  <DotsThree width={50} r={25} size={20} className="icon" />
                )}
              </td>
            </tr>
          );
        })}
      </>
    );
  };
  const renderTableBody = () => {
    if (name === "#IA") {
      return vdsUserTableBody();
    } else if (name === "#AU") {
      return vdsUserTableBodyAPI();
    } else if ([BRANCH_ADMIN, BRANCH_CONSULTANT].includes(name)) {
      return vdsUserTableBody();
    } else if (name === "branches") {
      return branchesTableBody();
    } else if (name === "Teams") {
      return TeamsTableBody();
    }
  };

  return (
    <div
      className="mt-2"
      style={{
        minHeight: tableStyle?.minHeight,
      }}
    >
      <SimpleScrollBar style={{ maxHeight: tableStyle?.maxHeight }}>
        <Table className="tableresponsive" responsive="md" hover>
          <thead>{renderHeaderRows()}</thead>
          <tbody>{renderTableBody()}</tbody>
        </Table>
      </SimpleScrollBar>
    </div>
  );
}
TableCommon.propTypes = {
  heading: PropTypes.any,
  body: PropTypes.any,
  name: PropTypes.string,
};
export default TableCommon;
